/**
 * Scout Report PDF Filename Generator (SE-4257)
 *
 * Generates standardised PDF filenames from scout report metadata using the convention:
 * - Single match: "[Player] vs [Opponent] on [Date].pdf"
 * - Two matches: "[Player] vs [Opp1], [Opp2] on [Date].pdf"
 * - 3+ matches: "[Player] - Multiple Matches on [Date].pdf"
 * - General report (no matches): "[Player] - General Report on [Date].pdf"
 *
 * Date logic: single/two matches use the earliest match date; 3+ matches and
 * general reports use the report creation date. Format is ISO YYYY-MM-DD.
 */

/** Maximum filename length including the .pdf extension */
const MAX_FILENAME_LENGTH = 200;

/** Characters invalid in filenames across Windows, macOS, and Linux */
const INVALID_FILENAME_CHARS = /[/\\:*?"<>|]/g;

/** Fallback filename when insufficient data is available */
const FALLBACK_FILENAME = "Scout Report.pdf";

/** Input data for generating a scout report filename */
export interface ScoutReportFilenameData {
  /** Player's display name */
  playerName?: string;
  /** Match details (opponents and dates) */
  matches?: { opponent?: string; matchDate?: string }[];
  /** ISO date string for when the report was created */
  reportDate?: string;
}

/**
 * Strips characters that are invalid in filenames while preserving
 * internationalized characters (accents, umlauts, Nordic letters, etc.)
 * @param text - Raw text to sanitize for use in a filename
 * @returns Text with invalid filename characters removed
 */
function sanitize(text: string): string {
  return text.replace(INVALID_FILENAME_CHARS, "");
}

/**
 * Extracts the ISO date portion (YYYY-MM-DD) from an ISO datetime string
 * @param dateString - Full ISO datetime string (e.g. "2026-02-15T15:00:00Z")
 * @returns Date-only string in YYYY-MM-DD format
 */
function toIsoDate(dateString: string): string {
  return dateString.slice(0, 10);
}

/**
 * Returns the earliest date string from match date strings
 * @param matches - Array of match objects with optional matchDate fields
 * @returns The earliest matchDate string, or undefined if none have dates
 */
function getEarliestMatchDate(
  matches: readonly { matchDate?: string }[]
): string | undefined {
  const datesWithValues = matches.filter(
    (m): m is { matchDate: string } => !!m.matchDate
  );
  if (datesWithValues.length === 0) return undefined;
  if (datesWithValues.length === 1) return datesWithValues[0].matchDate;

  return datesWithValues.reduce(
    (earliest, current) =>
      current.matchDate < earliest.matchDate ? current : earliest,
    datesWithValues[0]
  ).matchDate;
}

/**
 * Builds the filename stem and date source based on the match scenario
 * @param sanitizedPlayer - Player name already sanitized for filename use
 * @param matches - Filtered array of matches with valid opponents
 * @param reportDate - ISO date string for when the report was created
 * @returns Object with the filename stem and the date string to use
 */
function buildStemAndDate(
  sanitizedPlayer: string,
  matches: { opponent?: string; matchDate?: string }[],
  reportDate: string | undefined
): { stem: string; dateSource: string | undefined } {
  const matchCount = matches.length;

  if (matchCount === 0) {
    return {
      stem: `${sanitizedPlayer} - General Report`,
      dateSource: reportDate,
    };
  }

  if (matchCount <= 2) {
    const opponents = matches
      .map(m => sanitize(m.opponent?.trim() ?? ""))
      .join(", ");
    return {
      stem: `${sanitizedPlayer} vs ${opponents}`,
      dateSource: getEarliestMatchDate(matches) ?? reportDate,
    };
  }

  return {
    stem: `${sanitizedPlayer} - Multiple Matches`,
    dateSource: reportDate,
  };
}

/**
 * Generates a standardised PDF filename from scout report metadata.
 * @param data - Report metadata including player name, matches, and dates
 * @returns Filename string ending in .pdf, max 200 characters
 * @example
 * generateScoutReportFilename({
 *   playerName: "Marcus Rashford",
 *   matches: [{ opponent: "Arsenal", matchDate: "2026-02-15T15:00:00Z" }],
 *   reportDate: "2026-02-16T10:00:00Z",
 * })
 * // => "Marcus Rashford vs Arsenal on 2026-02-15.pdf"
 */
export function generateScoutReportFilename(
  data: ScoutReportFilenameData
): string {
  const playerName = data.playerName?.trim();
  if (!playerName) return FALLBACK_FILENAME;

  const sanitizedPlayer = sanitize(playerName);
  const matches = data.matches?.filter(m => m.opponent?.trim()) ?? [];

  const { stem, dateSource } = buildStemAndDate(
    sanitizedPlayer,
    matches,
    data.reportDate
  );

  const dateSuffix = dateSource ? ` on ${toIsoDate(dateSource)}` : "";
  const extension = ".pdf";
  const fullName = `${stem}${dateSuffix}${extension}`;

  if (fullName.length <= MAX_FILENAME_LENGTH) return fullName;

  // Truncate the stem to fit within the limit, preserving the date and extension
  const fixedSuffix = `${dateSuffix}${extension}`;
  const maxStemLength = MAX_FILENAME_LENGTH - fixedSuffix.length;
  const truncatedStem = stem.slice(0, maxStemLength).trimEnd();
  return `${truncatedStem}${fixedSuffix}`;
}
